import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Upload, 
  Mic, 
  FileAudio, 
  Copy, 
  Check, 
  Loader2, 
  FileText,
  AlertCircle,
  Trash2,
  ArrowRight
} from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';

const SUPPORTED_FORMATS = ['mp3', 'm4a', 'wav', 'webm', 'mp4', 'mpeg', 'mpga'];
const MAX_FILE_SIZE_MB = 25;

export default function TranscriptionPage() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [transcriptionInfo, setTranscriptionInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Validate format
    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!SUPPORTED_FORMATS.includes(ext)) {
      setError(`Formato não suportado: .${ext}. Use: ${SUPPORTED_FORMATS.map(f => `.${f}`).join(', ')}`);
      return;
    }

    // Validate size
    const sizeMB = selectedFile.size / (1024 * 1024);
    if (sizeMB > MAX_FILE_SIZE_MB) {
      setError(`Arquivo muito grande (${sizeMB.toFixed(1)} MB). Máximo: ${MAX_FILE_SIZE_MB} MB`);
      return;
    }

    setFile(selectedFile);
    setError('');
    setTranscription('');
    setTranscriptionInfo(null);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      const fakeEvent = { target: { files: [droppedFile] } };
      handleFileSelect(fakeEvent);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleTranscribe = async () => {
    if (!file) {
      toast.error('Selecione um arquivo de áudio');
      return;
    }

    setIsTranscribing(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.transcribeWithWhisper(formData);

      if (response.success) {
        setTranscription(response.transcription);
        setTranscriptionInfo({
          duration: response.duration,
          model: response.model,
          language: response.language
        });
        
        if (response.warning) {
          toast.warning(response.warning);
        } else {
          toast.success('Transcrição concluída!');
        }
      } else {
        throw new Error(response.detail || 'Erro na transcrição');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Erro ao transcrever áudio';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(transcription);
      setCopied(true);
      toast.success('Texto copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Erro ao copiar texto');
    }
  };

  const handleClear = () => {
    setFile(null);
    setTranscription('');
    setTranscriptionInfo(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUseForDocumentation = () => {
    // Store transcription in sessionStorage and navigate to new consultation
    sessionStorage.setItem('transcriptionText', transcription);
    navigate('/consultations/new', { state: { transcription } });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(0)} KB` : `${mb.toFixed(1)} MB`;
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="transcription-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-semibold tracking-tight ${isDark ? 'text-[#F0F2FF]' : 'text-slate-900'} mb-2`}>
            Transcrição de Consulta
          </h1>
          <p className={isDark ? 'text-[#9BA3BF]' : 'text-slate-600'}>
            Converta áudios de consultas em texto usando inteligência artificial (Groq Whisper)
          </p>
        </div>

        {/* LGPD Notice */}
        <Card className={`mb-6 ${isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className={`w-5 h-5 mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                Conformidade LGPD
              </p>
              <p className={`text-sm ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                O arquivo de áudio <strong>não é armazenado</strong>. Apenas o texto transcrito é retornado e pode ser salvo por você.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-6">
          {/* Upload Section */}
          <Card className={isDark ? 'bg-[#1A1D27] border-[#2A2E42]' : ''}>
            <CardHeader>
              <CardTitle className={isDark ? 'text-[#F0F2FF]' : ''}>
                <FileAudio className="w-5 h-5 inline mr-2" />
                Upload de Áudio
              </CardTitle>
              <CardDescription className={isDark ? 'text-[#9BA3BF]' : ''}>
                Formatos suportados: {SUPPORTED_FORMATS.map(f => `.${f}`).join(', ')} | Máximo: {MAX_FILE_SIZE_MB} MB
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Drop Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
                className={`
                  border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                  ${isDark 
                    ? 'border-[#2A2E42] hover:border-primary/50 hover:bg-[#22263A]' 
                    : 'border-slate-300 hover:border-primary/50 hover:bg-slate-50'}
                  ${file ? (isDark ? 'border-primary/50 bg-[#22263A]' : 'border-primary/50 bg-primary/5') : ''}
                `}
                data-testid="drop-zone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={SUPPORTED_FORMATS.map(f => `.${f}`).join(',')}
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
                
                {file ? (
                  <div className="space-y-2">
                    <FileAudio className={`w-12 h-12 mx-auto ${isDark ? 'text-primary' : 'text-primary'}`} />
                    <p className={`font-medium ${isDark ? 'text-[#F0F2FF]' : 'text-slate-900'}`}>
                      {file.name}
                    </p>
                    <p className={`text-sm ${isDark ? 'text-[#9BA3BF]' : 'text-slate-500'}`}>
                      {formatFileSize(file.size)}
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); handleClear(); }}
                      className={isDark ? 'border-[#2A2E42] text-[#C8CEDE]' : ''}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Remover
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className={`w-12 h-12 mx-auto ${isDark ? 'text-[#5C6480]' : 'text-slate-400'}`} />
                    <p className={`font-medium ${isDark ? 'text-[#F0F2FF]' : 'text-slate-900'}`}>
                      Arraste um arquivo ou clique para selecionar
                    </p>
                    <p className={`text-sm ${isDark ? 'text-[#9BA3BF]' : 'text-slate-500'}`}>
                      Suporta gravações de consultas em português
                    </p>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'}`}>
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  {error}
                </div>
              )}

              {/* Transcribe Button */}
              <Button
                onClick={handleTranscribe}
                disabled={!file || isTranscribing}
                className="w-full mt-4"
                data-testid="transcribe-btn"
              >
                {isTranscribing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Transcrevendo...
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4 mr-2" />
                    Transcrever Áudio
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Transcription Result */}
          {transcription && (
            <Card className={isDark ? 'bg-[#1A1D27] border-[#2A2E42]' : ''} data-testid="transcription-result">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className={isDark ? 'text-[#F0F2FF]' : ''}>
                    <FileText className="w-5 h-5 inline mr-2" />
                    Transcrição
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {transcriptionInfo?.duration && (
                      <Badge variant="secondary" className={isDark ? 'bg-[#22263A] text-[#9BA3BF]' : ''}>
                        {formatDuration(transcriptionInfo.duration)}
                      </Badge>
                    )}
                    <Badge variant="outline" className={isDark ? 'border-[#2A2E42] text-[#9BA3BF]' : ''}>
                      Whisper
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Transcription Text */}
                <div className={`
                  p-4 rounded-lg min-h-[200px] max-h-[400px] overflow-y-auto whitespace-pre-wrap
                  ${isDark ? 'bg-[#1E2130] text-[#F0F2FF]' : 'bg-slate-50 text-slate-900'}
                `}>
                  {transcription}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 mt-4">
                  <Button
                    variant="outline"
                    onClick={handleCopy}
                    className={isDark ? 'border-[#2A2E42] text-[#C8CEDE]' : ''}
                    data-testid="copy-btn"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-2 text-green-500" />
                        Copiado!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copiar Texto
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={handleUseForDocumentation}
                    className="bg-primary hover:bg-primary-hover"
                    data-testid="use-for-doc-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Gerar Documentação Clínica
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info Section */}
          <Card className={isDark ? 'bg-[#1A1D27] border-[#2A2E42]' : ''}>
            <CardHeader>
              <CardTitle className={`text-lg ${isDark ? 'text-[#F0F2FF]' : ''}`}>
                Como funciona
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className={`p-4 rounded-lg ${isDark ? 'bg-[#1E2130]' : 'bg-slate-50'}`}>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                    <Upload className="w-5 h-5 text-primary" />
                  </div>
                  <h4 className={`font-medium mb-1 ${isDark ? 'text-[#F0F2FF]' : 'text-slate-900'}`}>
                    1. Upload do Áudio
                  </h4>
                  <p className={`text-sm ${isDark ? 'text-[#9BA3BF]' : 'text-slate-600'}`}>
                    Faça upload da gravação da consulta (MP3, M4A, WAV)
                  </p>
                </div>
                
                <div className={`p-4 rounded-lg ${isDark ? 'bg-[#1E2130]' : 'bg-slate-50'}`}>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                    <Mic className="w-5 h-5 text-primary" />
                  </div>
                  <h4 className={`font-medium mb-1 ${isDark ? 'text-[#F0F2FF]' : 'text-slate-900'}`}>
                    2. Transcrição IA
                  </h4>
                  <p className={`text-sm ${isDark ? 'text-[#9BA3BF]' : 'text-slate-600'}`}>
                    Groq Whisper converte o áudio em texto em português
                  </p>
                </div>
                
                <div className={`p-4 rounded-lg ${isDark ? 'bg-[#1E2130]' : 'bg-slate-50'}`}>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <h4 className={`font-medium mb-1 ${isDark ? 'text-[#F0F2FF]' : 'text-slate-900'}`}>
                    3. Gerar Documentação
                  </h4>
                  <p className={`text-sm ${isDark ? 'text-[#9BA3BF]' : 'text-slate-600'}`}>
                    Use o texto para gerar prontuário clínico estruturado
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
